import React from 'react';

const cardShadow = { boxShadow: '10px 13px 0px #898db740' };

interface BulletProps {
  icon: string;
  text: string;
  textColor?: string;
}

const Bullet: React.FC<BulletProps> = ({ icon, text, textColor = 'text-black' }) => (
  <div className="flex gap-3 items-start">
    <img className="w-5 h-[15px] mt-1 flex-shrink-0" src={icon} alt="" />
    <p className={`font-proxima ${textColor} text-sm lg:text-base tracking-[0.8px] leading-normal`}>{text}</p>
  </div>
);

const HowWeWorkSection: React.FC = () => {
  const checkDark = require('../assets/img/how-we-work/checkmark-dark.svg').default;
  const checkWhite = require('../assets/img/how-we-work/checkmark-white.svg').default;

  return (
    <section className="py-12 lg:py-20 relative overflow-hidden">
      {/* Background pattern */}
      <img
        className="absolute inset-0 w-full h-full object-cover"
        src={require('../assets/img/how-we-work/bg-pattern.svg').default}
        alt=""
      />
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)' }}
      />

      <div className="max-w-content mx-auto px-6 relative z-10">
        <h2 className="font-exo font-bold text-ebony-clay text-2xl lg:text-[38px] tracking-[1.9px] leading-normal text-center mb-4">
          How we work
        </h2>
        <p className="font-proxima text-sm lg:text-base tracking-[0.8px] leading-normal text-center mb-10 lg:mb-12">
          <span className="text-black">We Plan Everything </span>
          <span className="text-royal-blue">&rarr;</span>
          <span className="text-black"> We Build Smart </span>
          <span className="text-royal-blue">&rarr;</span>
          <span className="text-black"> We Verify Quality </span>
          <span className="text-royal-blue">&rarr;</span>
          <span className="text-black"> You Get Results</span>
        </p>

        <div className="flex flex-col md:flex-row gap-6 lg:gap-8 justify-center items-stretch relative">
          {/* Card 1: Understanding Before Building (white) */}
          <div className="relative w-full md:w-[387px] flex-shrink-0 border-2 border-wistful rounded overflow-hidden md:mt-8" style={cardShadow}>
            <div className="bg-white border border-black p-6 lg:p-8 pb-24 lg:pb-28 flex flex-col h-full relative">
              <div className="relative w-[54px] h-[52px] mb-4 lg:mb-6 scale-75 origin-top-left lg:scale-100">
                <img className="w-[52px] h-[52px]" src={require('../assets/img/how-we-work/badge-1.svg').default} alt="" />
                <div className="absolute left-5 top-[5px] font-exo font-bold text-white text-2xl lg:text-[32px] tracking-[1.6px] leading-normal">1</div>
              </div>
              <h3 className="relative z-10 font-proxima-bold font-bold text-black text-lg lg:text-xl tracking-[1px] leading-normal">
                We Start with your vision
              </h3>
              <div className="relative z-10 font-proxima text-royal-blue text-sm lg:text-base tracking-[0.8px] leading-normal mb-6">
                Understanding before building
              </div>
              <div className="relative z-10 space-y-4 flex-grow">
                <Bullet icon={checkDark} text="We listen to what you need and document it clearly" />
                <Bullet icon={checkDark} text="Create a complete blueprint of your solution" />
                <Bullet icon={checkDark} text="Design how everything will look and work" />
                <Bullet icon={checkDark} text="You approve everything before we build" />
              </div>
              {/* Circles + icon in bottom-right corner */}
              <div className="absolute -right-20 -bottom-20 w-[200px] lg:w-[253px] h-[200px] lg:h-[253px]">
                <div className="absolute inset-0 opacity-[0.18] pointer-events-none">
                  <div className="absolute inset-0 rounded-full bg-royal-blue opacity-[0.15]" />
                  <div className="absolute left-4 lg:left-6 top-4 lg:top-6 w-[160px] lg:w-[206px] h-[160px] lg:h-[206px] rounded-full bg-royal-blue opacity-50" />
                  <div className="absolute left-8 lg:left-12 top-8 lg:top-12 w-[120px] lg:w-[157px] h-[120px] lg:h-[157px] rounded-full bg-royal-blue" />
                </div>
              </div>
              <img className="absolute right-7 bottom-7 lg:right-10 lg:bottom-10 w-7 h-7 lg:w-10 lg:h-10 object-contain z-0 pointer-events-none" src={require('../assets/img/how-we-work/icon-card1.svg').default} alt="" />
            </div>
          </div>

          {/* Card 2: Smart Building Process (blue) */}
          <div className="relative w-full md:w-[387px] flex-shrink-0 border-2 border-wistful rounded overflow-hidden" style={cardShadow}>
            <div className="bg-royal-blue border border-black p-6 lg:p-8 flex flex-col h-full relative">
              <div className="relative w-[54px] h-[52px] mb-4 lg:mb-6 scale-75 origin-top-left lg:scale-100">
                <img className="w-[52px] h-[52px]" src={require('../assets/img/how-we-work/badge-2.svg').default} alt="" />
                <div className="absolute left-[17px] top-[5px] font-exo font-bold text-royal-blue text-2xl lg:text-[32px] tracking-[1.6px] leading-normal">2</div>
              </div>
              <h3 className="font-proxima-extrabold text-white text-lg lg:text-xl tracking-[1px] leading-normal">
                Smart Building Process
              </h3>
              <div className="text-sm lg:text-base tracking-[0.8px] leading-normal mb-6">
                <span className="font-proxima-medium text-white">Technology </span>
                <span className="font-proxima text-white">+ </span>
                <span className="font-proxima-bold font-bold text-white">Human Expertise</span>
              </div>
              <div className="relative z-10 space-y-4 flex-grow">
                <Bullet icon={checkWhite} text="We use advanced tools to build faster" textColor="text-white font-proxima-medium" />
                <Bullet icon={checkWhite} text="Expert developers oversee everything" textColor="text-white font-proxima-medium" />
                <Bullet icon={checkWhite} text="Double-check all work for quality" textColor="text-white font-proxima-medium" />
                <Bullet icon={checkWhite} text="You approve everything before we build" textColor="text-white font-proxima-medium" />
              </div>
              {/* Desktop: spacer keeps the card tall so the centered watermark logo
                  (below, hidden on mobile) sits at the bottom of this card. */}
              <div className="hidden md:flex justify-center mt-40">
                <img className="w-[48px] h-[28px]" src={require('../assets/img/how-we-work/icon-card2.svg').default} alt="" />
              </div>
              {/* Mobile: the watermark is hidden, so render the white hexagon logo inline. */}
              <div className="flex md:hidden justify-center mt-10">
                <div className="relative w-[95px] h-[89px]">
                  <img className="absolute left-0 top-[18px] w-[18px] h-[31px]" src={require('../assets/img/how-we-work/logo-1.svg').default} alt="" />
                  <img className="absolute left-[18px] top-0 w-[58px] h-[45px]" src={require('../assets/img/how-we-work/logo-2.svg').default} alt="" />
                  <img className="absolute left-[42px] top-[32px] w-[52px] h-[46px]" src={require('../assets/img/how-we-work/logo-3.svg').default} alt="" />
                  <img className="absolute left-[42px] top-[70px] w-[34px] h-[19px]" src={require('../assets/img/how-we-work/logo-4.svg').default} alt="" />
                  <img className="absolute left-[58px] top-0 w-[30px] h-[30px]" src={require('../assets/img/how-we-work/logo-5.svg').default} alt="" />
                  <img className="absolute left-[10px] top-[29px] w-[35px] h-[49px]" src={require('../assets/img/how-we-work/logo-6.svg').default} alt="" />
                </div>
              </div>
            </div>
          </div>

          {/* Card 3: Quality Without the Wait (white) */}
          <div className="relative w-full md:w-[387px] flex-shrink-0 border-2 border-wistful rounded overflow-hidden md:mt-8" style={cardShadow}>
            <div className="bg-white border border-black p-6 lg:p-8 pb-24 lg:pb-28 flex flex-col h-full relative">
              <div className="relative w-[54px] h-[52px] mb-4 lg:mb-6 scale-75 origin-top-left lg:scale-100">
                <img className="w-[52px] h-[52px]" src={require('../assets/img/how-we-work/badge-3.svg').default} alt="" />
                <div className="absolute left-[17px] top-[5px] font-exo font-bold text-white text-2xl lg:text-[32px] tracking-[1.6px] leading-normal">3</div>
              </div>
              <h3 className="relative z-10 font-proxima-bold font-bold text-black text-lg lg:text-xl tracking-[1px] leading-normal">
                We Start with your vision
              </h3>
              <div className="relative z-10 font-proxima text-royal-blue text-sm lg:text-base tracking-[0.8px] leading-normal mb-6">
                Quality Without the Wait
              </div>
              <div className="relative z-10 space-y-4 flex-grow">
                <Bullet icon={checkDark} text="Your software ready in weeks, not months" />
                <Bullet icon={checkDark} text="Works right the first time" />
                <Bullet icon={checkDark} text="Complete user guides and documentation" />
                <Bullet icon={checkDark} text="You approve everything before we build" />
              </div>
              {/* Circles + icon in bottom-right corner */}
              <div className="absolute -right-20 -bottom-20 w-[200px] lg:w-[253px] h-[200px] lg:h-[253px]">
                <div className="absolute inset-0 opacity-[0.18] pointer-events-none">
                  <div className="absolute inset-0 rounded-full bg-royal-blue opacity-[0.15]" />
                  <div className="absolute left-4 lg:left-6 top-4 lg:top-6 w-[160px] lg:w-[206px] h-[160px] lg:h-[206px] rounded-full bg-royal-blue opacity-50" />
                  <div className="absolute left-8 lg:left-12 top-8 lg:top-12 w-[120px] lg:w-[157px] h-[120px] lg:h-[157px] rounded-full bg-royal-blue" />
                </div>
              </div>
              <img className="absolute right-7 bottom-7 lg:right-10 lg:bottom-10 w-7 h-7 lg:w-10 lg:h-10 object-contain z-0 pointer-events-none" src={require('../assets/img/how-we-work/icon-card3.svg').default} alt="" />
            </div>
          </div>

          {/* Logo watermark between cards — hidden on mobile */}
          <div className="absolute left-1/2 -translate-x-1/2 bottom-[40px] w-[119px] h-[112px] overflow-hidden hidden md:block pointer-events-none">
            <img className="absolute left-0 top-[23px] w-[22px] h-[39px]" src={require('../assets/img/how-we-work/logo-1.svg').default} alt="" />
            <img className="absolute left-[23px] top-0 w-[73px] h-[56px]" src={require('../assets/img/how-we-work/logo-2.svg').default} alt="" />
            <img className="absolute left-[53px] top-[40px] w-[65px] h-[57px]" src={require('../assets/img/how-we-work/logo-3.svg').default} alt="" />
            <img className="absolute left-[53px] top-[88px] w-[43px] h-[24px]" src={require('../assets/img/how-we-work/logo-4.svg').default} alt="" />
            <img className="absolute left-[72px] top-0 w-[38px] h-[37px]" src={require('../assets/img/how-we-work/logo-5.svg').default} alt="" />
            <img className="absolute left-[13px] top-[36px] w-[44px] h-[61px]" src={require('../assets/img/how-we-work/logo-6.svg').default} alt="" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowWeWorkSection;
